import { toast } from "react-hot-toast";

/** Hard cap for any object stored in Supabase (bucket limit). */
export const FILE_SIZE_BYTES = {
  IMAGE: 10 * 1024 * 1024,
  VIDEO: 50 * 1024 * 1024,
  ATTACHMENT: 20 * 1024 * 1024,
  STORAGE_OBJECT: 50 * 1024 * 1024,
} as const;

export type FileKind = "image" | "video" | "document";

const DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
]);

const DEFAULT_DOCUMENT_EXTENSIONS = [".pdf", ".doc", ".docx", ".txt"] as const;

export type ValidateFileOptions = {
  kinds?: readonly FileKind[];
  maxSize?: number;
  maxSizeByKind?: Partial<Record<FileKind, number>>;
  allowedDocumentExtensions?: readonly string[];
};

export type ValidateFileError =
  | { code: "too_large"; kind: FileKind; maxSize: number }
  | { code: "invalid_kind"; kind: FileKind };

export type ValidateFileResult =
  | { ok: true; kind: FileKind }
  | { ok: false; error: ValidateFileError };

export type FileValidationPreset =
  | "catalogImage"
  | "activityMedia"
  | "supportAttachment"
  | "receipt";

export const FILE_VALIDATION_PRESETS: Record<
  FileValidationPreset,
  ValidateFileOptions
> = {
  catalogImage: {
    kinds: ["image"],
    maxSize: FILE_SIZE_BYTES.IMAGE,
  },
  activityMedia: {
    kinds: ["image", "video"],
    maxSizeByKind: {
      image: FILE_SIZE_BYTES.IMAGE,
      video: FILE_SIZE_BYTES.VIDEO,
    },
  },
  supportAttachment: {
    kinds: ["image", "video", "document"],
    maxSize: FILE_SIZE_BYTES.ATTACHMENT,
    allowedDocumentExtensions: DEFAULT_DOCUMENT_EXTENSIONS,
  },
  receipt: {
    kinds: ["image", "document"],
    maxSize: FILE_SIZE_BYTES.IMAGE,
    allowedDocumentExtensions: [".pdf"],
  },
};

function fileExtension(file: File): string {
  const index = file.name.lastIndexOf(".");
  return index === -1 ? "" : file.name.slice(index).toLowerCase();
}

function isSupportedDocument(
  file: File,
  allowedExtensions: readonly string[],
): boolean {
  if (DOCUMENT_MIME_TYPES.has(file.type)) {
    return allowedExtensions.some((extension) => {
      if (extension === ".pdf") {
        return file.type === "application/pdf" || fileExtension(file) === ".pdf";
      }
      if (extension === ".doc") {
        return file.type === "application/msword" || fileExtension(file) === ".doc";
      }
      if (extension === ".docx") {
        return (
          file.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          fileExtension(file) === ".docx"
        );
      }
      if (extension === ".txt") {
        return file.type === "text/plain" || fileExtension(file) === ".txt";
      }
      return fileExtension(file) === extension;
    });
  }

  const extension = fileExtension(file);
  return allowedExtensions.includes(extension);
}

export function getFileKind(file: File): FileKind {
  if (file.type.startsWith("image/")) {
    return "image";
  }
  if (file.type.startsWith("video/")) {
    return "video";
  }
  return "document";
}

function resolveMaxSize(file: File, options: ValidateFileOptions): number | undefined {
  const kind = getFileKind(file);
  const byKind = options.maxSizeByKind?.[kind];
  if (byKind != null) {
    return byKind;
  }
  return options.maxSize;
}

function isAllowedKind(file: File, options: ValidateFileOptions): boolean {
  const kinds = options.kinds;
  if (!kinds || kinds.length === 0) {
    return true;
  }

  const kind = getFileKind(file);
  if (!kinds.includes(kind)) {
    return false;
  }

  if (kind !== "document") {
    return true;
  }

  const allowedExtensions =
    options.allowedDocumentExtensions ?? DEFAULT_DOCUMENT_EXTENSIONS;
  return isSupportedDocument(file, allowedExtensions);
}

export function validateFile(
  file: File,
  options: ValidateFileOptions = {},
): ValidateFileResult {
  const kind = getFileKind(file);

  if (!isAllowedKind(file, options)) {
    return { ok: false, error: { code: "invalid_kind", kind } };
  }

  const maxSize = resolveMaxSize(file, options);
  if (maxSize != null && file.size > maxSize) {
    return { ok: false, error: { code: "too_large", kind, maxSize } };
  }

  return { ok: true, kind };
}

export function validateFileWithPreset(
  file: File,
  preset: FileValidationPreset,
): ValidateFileResult {
  return validateFile(file, FILE_VALIDATION_PRESETS[preset]);
}

export function formatFileSizeLimit(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  return Number.isInteger(megabytes)
    ? `${megabytes}MB`
    : `${megabytes.toFixed(1)}MB`;
}

export function partitionValidatedFiles(
  files: Iterable<File>,
  options: ValidateFileOptions,
): {
  accepted: Array<{ file: File; kind: FileKind }>;
  rejected: Array<{ file: File; error: ValidateFileError }>;
} {
  const accepted: Array<{ file: File; kind: FileKind }> = [];
  const rejected: Array<{ file: File; error: ValidateFileError }> = [];

  for (const file of files) {
    const result = validateFile(file, options);
    if (result.ok) {
      accepted.push({ file, kind: result.kind });
    } else {
      rejected.push({ file, error: result.error });
    }
  }

  return { accepted, rejected };
}

export type RejectedFileToastMessages = {
  tooLarge: (file: File, maxSize: number) => string;
  unsupported: (file: File) => string;
};

export function toastRejectedFiles(
  rejected: Array<{ file: File; error: ValidateFileError }>,
  messages: RejectedFileToastMessages,
): void {
  for (const { file, error } of rejected) {
    if (error.code === "too_large") {
      toast.error(messages.tooLarge(file, error.maxSize));
    } else {
      toast.error(messages.unsupported(file));
    }
  }
}

export type ReceiptFileToastMessages = {
  tooLarge: (maxSizeLabel: string) => string;
  unsupported: string;
};

/** Validate a receipt upload; toast and return false on failure. */
export function validateReceiptFileOrToast(
  file: File,
  messages: ReceiptFileToastMessages,
): boolean {
  const result = validateFileWithPreset(file, "receipt");
  if (!result.ok) {
    if (result.error.code === "too_large") {
      toast.error(messages.tooLarge(formatFileSizeLimit(result.error.maxSize)));
    } else {
      toast.error(messages.unsupported);
    }
    return false;
  }
  return true;
}
